import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { Row, Space, Typography } from 'antd';
import { setPmState } from '../../../store/entities/pm';
import moment from 'moment';
import { toLower } from 'lodash';
import * as echarts from 'echarts';
import { totallyDarkeChartsTheme } from '../../../util/theme';
import { ECHARTS_LOADING_OPTION, USE_DARK_MODE } from '../../../util/constants';
import generateGuid from '../../../util/guid';

const PPDChart = (props) => {
  const dispatch = useDispatch();
  const {
    ppdType,
    ppdMain,
    apiUrl,
    apiUrlAgg,
    chartTitle,
    chartWidth,
    chartHeight,
    movement,
    direction,
  } = props;
  const rawPPDData = props.ppdData;
  const [infoBox, set_infoBox] = useState(null);
  const echartRef = useRef(null);
  const sessionId = sessionStorage.getItem('sessionId');
  const yAxisTitle = 'Distance to the Far Side (ft.)';
  const yStart = -500;
  const yEnd = 1320;
  const colors = {
    0: '#32d700',
    1: '#FFA200',
    2: '#FF3E3E',
    3: '#d900f2',
  };

  useEffect(async () => {
    if (props.aggData && props.aggData.length) {
      const trajCountDict = props.aggData
      .filter(d => {
        if (ppdMain) {
          return d.direction === direction;
        } else {
          return toLower(d.movement) === movement && d.direction === direction;
        }
      })
      .reduce((acc, val) => {
          acc.aogCnt += val.aogCnt;
          acc.sfCnt += val.sfCnt;
          acc.dsbCnt += val.dsbCnt;
          acc.trajCnt += val.trajCnt;
          return acc;
        },
        {
          aogCnt: 0,
          sfCnt: 0,
          dsbCnt: 0,
          trajCnt: 0
        });
      getPPDData(echartRef,
        apiUrl,
        chartTitle,
        yStart,
        yEnd,
        colors,
        set_infoBox,
        trajCountDict.sfCnt,
        trajCountDict.dsbCnt,
        ppdType,
        ppdMain,
        dispatch,
        sessionId,
        movement,
        direction,
        rawPPDData);
    }
  }, [apiUrl, apiUrlAgg, sessionId, rawPPDData, props.aggData]);

  echarts.registerTheme('totally-dark-theme', totallyDarkeChartsTheme);

  return (
    <>
      <Row style={{
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {infoBox}
      </Row>
      <Row>
        <ReactECharts
          ref={echartRef}
          showLoading={props.isLoading}
          loadingOption={ECHARTS_LOADING_OPTION}
          theme={USE_DARK_MODE ? 'totally-dark-theme' : ''}
          style={{
            width: `${chartWidth}px`,
            height: `${chartHeight}px`
          }}
          option={
            {
              tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                  // Get your data from params, eg: params[0].data
                }
              },
              grid: { show: true },
              legend: { show: true },
              title: {
                textStyle: {
                  fontWeight: 'bold',
                },
                x: 'center'
              },
              xAxis: {
                min: -150,
                max: 50,
                // show: false,
                type: 'value',
                name: 'Time to the Intersection\'s Far Side (s)',
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: {
                  fontWeight: 'bold'
                },
                show: true,
                showGrid: true,
                axisLine: { show: true },
                splitLine: { show: true },
                axisTick: { show: true },
                axisLabel: { show: true }
              },
              yAxis: {
                min: yStart,
                max: yEnd,
                // show: false,
                type: 'value',
                name: yAxisTitle,
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: {
                  fontWeight: 'bold'
                },
                show: true,
                showGrid: true,
                axisLine: { show: true },
                splitLine: { show: true },
                axisTick: { show: true },
                axisLabel: { show: true },
              },
            }
          }
        />
      </Row>
    </>
  );
};

const getPPDData = (echartRef, apiUrl, chartTitle, yStart, yEnd, colors, set_infoBox, sf, dsb, ppdType, ppdMain, dispatch, sessionId, movement, direction, rawPPDData) => {
  if (!rawPPDData.length) return;
  if (echartRef.current !== null) {
    const echartInstance = echartRef.current.getEchartsInstance();
    const wejoChartOptions = echartInstance.getOption();
    wejoChartOptions.series = [{
      type: 'lines',
      symbol: 'none',
      silent: true,
      animation: true,
      smooth: true,
      polyline: true,
      data: [],
      clip: true,
      largeThreshold: 1000000,
      large: false,
      progressive: 0,
      coordinateSystem: 'cartesian2d'
    }];
    echartInstance.setOption(wejoChartOptions);
    wejoChartOptions.series[0].data = [];
    // const result = await axios.get(apiUrl, {headers: {Authorization: `Bearer ${sessionId}`}});
    // const parsed = Papa.parse(result.data, {header: true, skipEmptyLines: true, dynamicTyping: true}).data;

    const ppdData = rawPPDData
    .filter(d => {
      if (ppdMain) {
        return d.direction === direction;
      } else {
        return toLower(d.movement) === movement && d.direction === direction;
      }
    })
    .map(d => ({
      UTCTimestampInit: moment.unix(d.timestamp)
      .toISOString(),
      journeyId: d.journeyId,
      Y_AXIS_FT: JSON.parse(d.dist_to_fs),
      X_AXIS_S: JSON.parse(d.time_to_fs),
      STOPS: d.stops_cnt,
      SPEEDL: d.ups_speed_limit,
      dow: moment.unix(d.timestamp)
      .day(),
    }));

    let is_first_traj = true;
    let speedLimit;
    let interWidth;
    let freeFlowData;
    let trajCount = {
      'total': 0,
      0: 0,
      1: 0,
      2: 0,
      3: 0
    };
    let journeyDirection;
    let journeyIdArray = [];
    for (let i in ppdData) {
      let journey = ppdData[i];
      let seriesData = [];
      // let stops = parseInt(journey['STOPS']['data'][0]); //for Node JS API
      let stops = parseInt(journey.STOPS); //for Python API
      let journeyId = journey.journeyId;
      if (ppdType === 'main') {
        if (!(journeyIdArray.includes(journeyId)) && journeyIdArray.length < 5) {
          journeyIdArray.push(journeyId);
        }
      }
      journeyDirection = journey.direction;
      trajCount.total = trajCount.total + 1;
      if (stops > 2) {
        trajCount[3] = trajCount[3] + 1;
      } else {
        trajCount[stops] = trajCount[stops] + 1;
      }
      if (is_first_traj) {
        speedLimit = journey.SPEEDL;
        interWidth = calcIntWidth(journey.X_AXIS_S, journey.Y_AXIS_FT);
        freeFlowData = freeFlowTrajectory(speedLimit, yStart, yEnd, interWidth);
        is_first_traj = false;
      }
      const [color, linewidth, alpha] = getColor(stops, colors);
      for (let j in journey.X_AXIS_S) {
        seriesData.push([journey.X_AXIS_S[j], journey.Y_AXIS_FT[j]]);
      }
      wejoChartOptions.series[0].data.push({
        coords: seriesData,
        lineStyle: {
          color: color,
          width: linewidth,
          opacity: alpha,
        },
      });
    }

    if (ppdType === 'main') {
      dispatch(setPmState({
        journeyIdArray: journeyIdArray
      }));
    }

    wejoChartOptions.series[0].data.push({
      coords: freeFlowData,
      lineStyle: {
        color: 'black',
        width: 2,
        opacity: 1,
      },
    });
    if (ppdType === 'single') updateInfoBox(trajCount, colors, set_infoBox, sf, dsb);
    if (ppdData.length > 0) {
      wejoChartOptions.title[0].text = chartTitle;
      echartInstance.setOption(wejoChartOptions);
    } else {
      wejoChartOptions.title[0].text = `${chartTitle} - NO DATA`;
      wejoChartOptions.series[0].data = [];
      echartInstance.setOption(wejoChartOptions);
    }

  }
};

const updateInfoBox = (trajCount, colors, set_infoBox, sf, dsb) => {
  const { Text } = Typography;
  const svgWidth = '30px';
  const svgHeight = '30px';
  const paddingSize = '10px';
  const rectSize = '20px';
  set_infoBox(
    <Row style={{
      width: '250px',
      marginBottom: '25px',
      justifyContent: 'left'
    }}>
      <Space direction={'vertical'} key={generateGuid()}>

        <Row>
          <Text strong>{`# Trajectories: ${trajCount.total}`}</Text>
        </Row>
        {[0, 1, 2, 3].map((idx) => (
          <Row key={idx}>
            <Text strong>
              <svg width={svgWidth} height={svgHeight} style={{ padding: { paddingSize } }}>
                <rect width={rectSize} height={rectSize} style={{ fill: colors[idx] }}/>
              </svg>
              {`${idx > 2 ? '>2' : idx} stops: ${trajCount[idx]} (${(trajCount[idx] / trajCount.total * 100).toFixed(
                0)}%)`}
            </Text>
          </Row>
        ))}

        <Row>
          <Text strong>
            {`Split failure: ${sf} (${(sf / trajCount.total * 100).toFixed(0)}%)`}
          </Text>
        </Row>
        <Row>
          <Text strong>
            {`Downstream Blockage: ${dsb} (${(dsb / trajCount.total * 100).toFixed(0)}%)`}
          </Text>
        </Row>
      </Space>
    </Row>
  );
};

const getColor = (stops, colors) => {
  let color;
  let linewidth;
  let alpha;
  switch (stops) {
    case 0:
      color = colors[0];
      linewidth = 1;
      alpha = 0.15;
      break;
    case 1:
      color = colors[1];
      linewidth = 1;
      alpha = 0.5;
      break;
    case 2:
      color = colors[2];
      linewidth = 1.5;
      alpha = 0.8;
      break;
    default:
      if (stops > 2) {
        color = colors[3];
        linewidth = 1.5;
        alpha = 1;
      } else {
        color = 'blue';
        linewidth = 1.5;
        alpha = 1;
      }
      break;
  }
  return [color, linewidth, alpha];
};

const calcIntWidth = (xArray, yArray) => {
  let time_objective = 0.0; // x2
  let time_pre_far_side; // x1
  let time_post_far_side; // x3
  let dist_pre_far_side; // y1
  let dist_post_far_side; // y3
  for (let idx in xArray) {
    if (xArray[idx] > 0) {
      time_pre_far_side = parseFloat(xArray[idx - 1]);
      time_post_far_side = parseFloat(xArray[idx]);
      dist_pre_far_side = parseFloat(yArray[idx - 1]);
      dist_post_far_side = parseFloat(yArray[idx]);
      break;
    }

  }

  //Linear interpolation
  let interWidth = Math.abs(((time_objective - time_pre_far_side) * (dist_post_far_side - dist_pre_far_side)) / (time_post_far_side - time_pre_far_side) + dist_pre_far_side);
  return interWidth;
};

const freeFlowTrajectory = (speedLimit, yStart, yEnd, interWidth) => {
  const buffer = 500; // Add a buffer to account for the intersection width
  const mph_to_fts = 1.467; // Multiply
  speedLimit *= mph_to_fts; // speed limit in ft/s
  const init_dist_to_sb = yEnd + buffer; // Initial distance to the stop bar
  const final_dist_from_sb = yStart; // Final distance to the stop bar
  const init_time_to_sb = (init_dist_to_sb / speedLimit) * -1.0; // Initial time. Since it is upstream, the time is negative
  const final_time_from_sb = (final_dist_from_sb / speedLimit) * -1.0; // Final time. Since the distance is negative, we make the number positive
  return [[init_time_to_sb, init_dist_to_sb - interWidth], [final_time_from_sb, final_dist_from_sb - interWidth]];
};

export default PPDChart;
